import React, { useState } from 'react';

import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import StreamTextDisplay from 'blockscope/components/aiSystem/panel/StreamTextDisplay';

/**
 * Renders a clean message with collapsible internal thought section
 * with markdown support using StreamTextDisplay
 * @param {Object} props - Component props
 * @param {Object|string} props.content - Message content
 */
const BaseResponse = ({ content }) => {
  const [showInternalThought, setShowInternalThought] = useState(false);

  // Parse content if it's a string
  let parsedContent;
  if (typeof content === 'string') {
    try {
      parsedContent = JSON.parse(content);
    } catch (e) {
      // If parsing fails, just display the content as text
      // Check if content might be markdown and use StreamTextDisplay
      return isLikelyMarkdown(content) ? (
        <div className='clean-message'>
          <StreamTextDisplay text={content} />
        </div>
      ) : (
        <div className='clean-message'>{content}</div>
      );
    }
  } else if (typeof content === 'object') {
    parsedContent = content;
  } else {
    return <div className='clean-message'>{String(content)}</div>;
  }

  // Extract data from parsed content
  const { messageText, internalThought, fullAnalysis } = parsedContent;

  // Function to detect if a string is likely markdown
  function isLikelyMarkdown(text) {
    // Check for common markdown patterns
    const markdownPatterns = [
      /^#+ /m, // Headers
      /\*\*.+\*\*/, // Bold text
      /\*.+\*/, // Italic text
      /\[.+\]\(.+\)/, // Links
      /```[\s\S]*?```/, // Code blocks
      /^\s*[-*+] /m, // Unordered lists
      /^\s*\d+\. /m, // Ordered lists
      /^\s*>.+/m, // Blockquotes
      /!\[.+\]\(.+\)/, // Images
      /\|.+\|.+\|/, // Tables
      /^---$/m, // Horizontal rules
      /==.+==$/m, // Highlighted text
      /~~.+~~$/m, // Strikethrough
    ];

    return markdownPatterns.some((pattern) => pattern.test(text));
  }

  return (
    <div className='clean-message-container'>
      {/* Main message - Use StreamTextDisplay if messageText is markdown */}
      <div className='message-text mb-3'>
        {messageText ? (
          isLikelyMarkdown(messageText) ? (
            <StreamTextDisplay text={messageText} />
          ) : (
            messageText
          )
        ) : (
          'No message text available'
        )}
      </div>

      {/* Full Analysis with markdown support if available */}
      {fullAnalysis && (
        <div className='mb-3'>
          <StreamTextDisplay text={fullAnalysis} />
        </div>
      )}

      {/* Internal Thought section - more compact */}
      {internalThought && (
        <div className='internal-thought-section mb-2'>
          <small
            className={`d-inline-flex align-items-center px-2 py-1 rounded cursor-pointer ${
              showInternalThought ? 'bg-primary bg-opacity-10' : 'bg-light'
            }`}
            onClick={() => setShowInternalThought(!showInternalThought)}
            style={{ cursor: 'pointer', fontSize: '0.85rem' }}
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className={`me-1 ${
                showInternalThought ? 'text-primary' : 'text-secondary'
              }`}
              style={{ fontSize: '0.8rem' }}
            />
            <span>Thought Process</span>
          </small>

          {showInternalThought && (
            <div className='content-body mt-1 ps-2 border-start border-2 border-light'>
              <small
                className='d-block py-1 text-secondary'
                style={{ fontSize: '0.85rem' }}
              >
                {isLikelyMarkdown(internalThought) ? (
                  <StreamTextDisplay text={internalThought} />
                ) : (
                  internalThought
                )}
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BaseResponse;

